import { catchCalculatorError, resultListFilter } from "@component/assets/AbstractListingCalculator/domain";
import type { MarketingFilter, ResultListResponse } from "@component/assets/AbstractListingCalculator/types";
import { CalculatorError } from "@component/assets/CalculatorError";
import { Config } from "@config";
import { formatText } from "@ui/format";
import type { StringPair } from "@utils/JsonDataBuilder";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import { http, __, generateUrlParams, getLocale } from "bm-ui-kit";
import type { FormCheckOptions } from "bm-ui-kit";
import dayjs from "dayjs";
import uniqBy from "lodash/uniqBy";
import type { MortgageLeadCalcRequest, MortgageLeadCalcResponse } from "../LeadForm/types";
import {
	CONFIG,
	DEFAULT_PARAMS,
	DEFAULT_PARAMS_FOR_BUILD,
	DEFAULT_PARAMS_FOR_CSOK,
	DEFAULT_PARAMS_FOR_CSOK_PLUSZ,
	DEFAULT_PARAMS_FOR_FREE_USE,
	DEFAULT_PARAMS_FOR_GREEN_LOAN,
	DEFAULT_PARAMS_FOR_HOUSE_LOAN,
	DEFAULT_PARAMS_FOR_LOAN_REDEMPTION,
	DEFAULT_PARAMS_FOR_RENOVATION,
	DEFAULT_PARAMS_FOR_SUPPORTED_RENOVATION,
	LOAN_GOALS,
} from "./data";
import type {
	InterestCategory,
	LoanGoal,
	MortgageListingCalculatorVariant,
	MortgageRequest,
	MortgageResponseItem,
	MortgageSearchParams,
	MortgageSort,
	PeriodCategory,
} from "./types";

const YEAR = 12;
const THREE_YEARS = 36;
const FIVE_YEARS = 60;
const TEN_YEARS = 120;
const THIRTY_YEARS = 360;

export const INTEREST_CATEGORY: InterestCategory[] = ["short", "five", "ten", "fix"];

function getInterestMonths(maturityYears: number, result: MortgageResponseItem): number {
	return Math.min(result.interestPeriodAided?.month || result.interestPeriodNotAided?.month, maturityYears * YEAR);
}

function isFixInterestCategory(maturityYears: number, interestPeriodMonths: number, result: MortgageResponseItem) {
	return (
		interestPeriodMonths >= maturityYears * YEAR ||
		result.csok ||
		(result.loanType === "stateSponsoredLoanHome" && (result.grantYears === 10 || result.grantYears === 25))
	);
}

export function getInterestCategory(maturityYears: number, result: MortgageResponseItem): InterestCategory {
	const interestPeriodMonths = getInterestMonths(maturityYears, result);

	if (isFixInterestCategory(maturityYears, interestPeriodMonths, result)) {
		return "fix";
	}

	if (interestPeriodMonths <= THREE_YEARS) {
		return "short";
	}

	if (THREE_YEARS < interestPeriodMonths && interestPeriodMonths <= FIVE_YEARS) {
		return "five";
	}

	if (FIVE_YEARS < interestPeriodMonths && interestPeriodMonths <= TEN_YEARS) {
		return "ten";
	}

	return "long";
}

export async function calculate(request: MortgageRequest): Promise<ResultListResponse<MortgageResponseItem>> {
	return http
		.post(Config.getApiUrl("/api/public/mortgages"), request)
		.then(
			(rawResults: {
				highlightList: MortgageResponseItem[];
				normalList: MortgageResponseItem[];
				status: number;
				fieldErrors: Record<string, string>;
				ko: string;
				message: string;
			}) => {
				if (rawResults.ko === "JTM") {
					throw new CalculatorError(
						rawResults.message,
						rawResults.fieldErrors,
						request,
						Config.getApiUrl("/api/public/mortgages"),
					);
				}

				if (rawResults.message) {
					throw new CalculatorError(
						rawResults.message,
						rawResults.fieldErrors,
						request,
						Config.getApiUrl("/api/public/mortgages"),
					);
				}

				return rawResults;
			},
		)
		.catch(catchCalculatorError);
}

function periodFilter(maturityYears: number, result: MortgageResponseItem, periodCategory: PeriodCategory): boolean {
	const interestPeriodMonths = getInterestMonths(maturityYears, result);

	if (periodCategory !== "FULL") {
		// a hitel futamideje nem hosszabb mint a kamatperiódusa, akkor végig fix
		if (isFixInterestCategory(maturityYears, interestPeriodMonths, result)) {
			return false;
		}
	}

	switch (periodCategory) {
		case "FULL":
			return isFixInterestCategory(maturityYears, interestPeriodMonths, result);

		case "THREE_MONTHS,SIX_MONTHS,ONE_YEAR,THREE_YEARS":
			return interestPeriodMonths <= THREE_YEARS;

		case "FIVE_YEARS":
			return THREE_YEARS < interestPeriodMonths && interestPeriodMonths <= FIVE_YEARS;

		case "TEN_YEARS":
			return FIVE_YEARS < interestPeriodMonths && interestPeriodMonths <= TEN_YEARS;

		case "FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS":
			return TEN_YEARS < interestPeriodMonths && interestPeriodMonths <= THIRTY_YEARS;

		default:
			return false;
	}
}

export function getGuestPeriodCategory(interestPeriods: PeriodCategory[]): PeriodCategory[] {
	const guestInterestPeriods: PeriodCategory[] = [];
	const various: PeriodCategory[] = ["THREE_MONTHS", "SIX_MONTHS", "ONE_YEAR", "THREE_YEARS"];

	if (interestPeriods.find((item) => various.includes(item))) {
		guestInterestPeriods.push("THREE_MONTHS,SIX_MONTHS,ONE_YEAR,THREE_YEARS");
	}
	guestInterestPeriods.push("FIVE_YEARS");
	guestInterestPeriods.push("TEN_YEARS");
	const fifteenTwentyYears: PeriodCategory[] = ["FIFTEEN_YEARS", "TWENTY_YEARS", "TWENTY_FIVE_YEARS", "THIRTY_YEARS"];

	if (interestPeriods.find((item) => fifteenTwentyYears.includes(item))) {
		guestInterestPeriods.push("FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS");
	}
	guestInterestPeriods.push("FULL");

	return guestInterestPeriods;
}

export function hasResultInPeriod(
	maturityYears: number,
	results: MortgageResponseItem[],
	periodCategory: PeriodCategory,
): boolean {
	return results.find((result) => periodFilter(maturityYears, result, periodCategory)) !== undefined;
}

export function getResultsInPeriod(
	maturityYears: number,
	results: MortgageResponseItem[],
	periodCategory: PeriodCategory,
): MortgageResponseItem[] {
	return results.filter((result) => periodFilter(maturityYears, result, periodCategory));
}

function converter(request: MortgageRequest, result: MortgageResponseItem): MortgageResponseItem {
	const newResult = { ...result };
	const { bank, marketing, apr } = newResult;
	const maturityMonths = Number(request.maturity) * 12;

	// Altípus átnevezése
	newResult.subType = result.subTypeMarking || result.subType;

	if (marketing?.newTargetUrl && !marketing.highlight && bank?.code !== "granit") {
		newResult.marketing.newTargetUrl = undefined;
	}

	// KH link paraméterezés
	if (bank.code === "kh" && marketing.newTargetUrl) {
		const params = {
			p_p_id: "contactform_WAR_contactformportlet",
			p_p_lifecycle: 0,
			_contactform_WAR_contactformportlet_amount: request.loanAmount,
			_contactform_WAR_contactformportlet_maturity: maturityMonths,
			_contactform_WAR_contactformportlet_installment: newResult.installmentStart,
			_contactform_WAR_contactformportlet_thm: Math.round(apr * 10000) / 100,
			_contactform_WAR_contactformportlet_interestType: "TEN_YEARS",
		};
		newResult.marketing.newTargetUrl += `${
			newResult.marketing.newTargetUrl.includes("?") ? "&" : "?"
		}${generateUrlParams(params)}`;
	}

	if (bank.code === "raiffeisen" && marketing.newTargetUrl) {
		const params = {
			hitelosszeg: request.loanAmount,
			futamido: request.maturity,
		};
		newResult.marketing.newTargetUrl += `${marketing.newTargetUrl.includes("?") ? "&" : "?"}${generateUrlParams(
			params,
		)}`;
	}

	return newResult;
}

export function responseToResult(
	request: MortgageRequest,
	response: any,
	filter: MarketingFilter,
): ResultListResponse<MortgageResponseItem> {
	const results = resultListFilter(response, filter, (item) => converter(request, item as MortgageResponseItem));

	if (getLocale() !== "hu_HU") {
		results.highlightList = [];
	}

	// Csak a highlightlist-ben szűrjük, a normal-ban megjelenhet egyik, másik vagy mindkettő is
	if (results.highlightList.find((item) => item.name === "K&H Minősített Fogyasztóbarát lakáshitel")) {
		// HA van a HL-ben nem fix verzió akkor ne legyen benne fix
		results.highlightList = results.highlightList.filter(
			(item) => item.name !== "K&H Minősített Fogyasztóbarát lakáshitel (fix)",
		);
	}

	const bankFilter = new Set<string>();
	results.highlightList = results.highlightList.filter((result) => {
		const { bank, marketing } = result;

		if (marketing.highlight) {
			if (bank.code === "unicredit") {
				if (bankFilter.has(bank.code)) {
					return false;
				}
				bankFilter.add(bank.code);
			}

			return true;
		}

		return false;
	});

	if (request.onePerBank) {
		results.normalList = uniqBy(
			results.normalList,
			(result) => result.bank.code + getInterestCategory(request.maturity, result),
		);
	}

	return results;
}

export function searchParamsToRequest(searchParams: MortgageSearchParams, sort: MortgageSort = "THM"): MortgageRequest {
	const {
		realEstateLocation,
		interestPeriods = [],
		interestPeriod,
		income,
		realEstateValue,
		monthlyDeposit,
		calculationDate,
		incomeList,
		onlyGreen,
		withEnergyEfficient,
		numberOfChildrenCommitment,
		numberOfChildrenCurrent,
		...other
	} = searchParams;

	const filteredInterestPeriods = interestPeriod
		? interestPeriod.split(",")
		: ([...new Set(interestPeriods.join(",").split(","))] as PeriodCategory[]);

	return {
		...other,
		sort,
		comparator: sort,
		roleCode: "BM",
		interestPeriods: filteredInterestPeriods,
		realEstateLocation: realEstateLocation === "" ? undefined : realEstateLocation,
		realEstateValue: realEstateValue || Math.min(CONFIG.MAX_REAL_ESTATE_VALUE, searchParams.loanAmount * 2),
		incomeList: incomeList || [
			{
				incomeType: "SALARY-H",
				incomeTypeName: "Magyar alkalmazotti jövedelem",
				amount: income,
				receiptAmount: searchParams.receiptOfIncome ? income : 0,
			},
		],
		withCoupon: true,
		monthlyDeposit: monthlyDeposit || undefined,
		mortgagedPropertyAcceptableTypes: ["LAKAS"],
		mortgagedPropertyAcceptableWallings: ["TEGLA_YTONG"],
		targetPropertyAcceptableTypes: ["LAKAS"],
		targetPropertyAcceptableWallings: ["TEGLA_YTONG"],
		income,
		calculationDate,
		onlyGreen,
		ecoFriendly: onlyGreen,
		withEnergyEfficient,
		numberOfChildren: numberOfChildrenCommitment + numberOfChildrenCurrent,
		numberOfChildrenCurrent: searchParams.numberOfChildrenCurrent,
		numberOfChildrenCommitment: searchParams.numberOfChildrenCommitment,
	};
}

export async function exportXls(searchParams: MortgageSearchParams): Promise<string> {
	const request: MortgageRequest = searchParamsToRequest(searchParams);

	return http
		.post(Config.getWpRestApiUrl("/post-process/MORTGAGE/export"), request)
		.then((response: { url: string }) => response.url);
}

export function getLeadCalculatorRequest(searchParams: MortgageSearchParams): MortgageLeadCalcRequest {
	const {
		age,
		numberOfChildren = searchParams.numberOfChildrenCommitment + searchParams.numberOfChildrenCurrent,
		realEstateLocation,
		currentInstallment,
		currentCreditLine,
		monthlyDeposit,
		receiptOfIncome,
		definedInsuranceUsage,
		activeAccountPackageUsage,
		loanCoverageInsurance,
		activeCreditCardUsage,
		definedAccountPackageUsage,
		incomeList,
		loanAmount,
		loanGoal,
		realEstateValue,
		maturity,
		income,
	} = searchParamsToRequest(searchParams);

	const otherData = new JsonDataBuilder();
	otherData.addItem("Életkor", "age", age);
	otherData.addItem("Gyermekek száma", "numberOfChildren", numberOfChildren);
	otherData.addItem("Ingatlan elhelyezkedése", "realEstateLocation", realEstateLocation);
	otherData.addItem("Meglévő hiteltörlesztés", "currentInstallment", currentInstallment, "ft");
	otherData.addItem("Meglévő hitelkeretek", "currentCreditLine", currentCreditLine, "ft");
	otherData.addItem("Havi megtakarítás", "monthlyDeposit", monthlyDeposit, "ft");
	otherData.addItem("Jövedelem bankhoziránytása", "receiptOfIncome", receiptOfIncome, "bool");
	otherData.addItem("Lakásbiztosítás", "definedInsuranceUsage", definedInsuranceUsage, "bool");
	otherData.addItem("Aktív számlahasználat", "activeAccountPackageUsage", activeAccountPackageUsage, "bool");
	otherData.addItem("Hitelfedezeti biztosítás", "loanCoverageInsurance", loanCoverageInsurance, "bool");
	otherData.addItem("Aktív kártyahasználat", "activeCreditCardUsage", activeCreditCardUsage, "bool");
	otherData.addItem("Számlavezetés", "definedAccountPackageUsage", definedAccountPackageUsage, "bool");

	return {
		otherDataJson: otherData.getJson(),
		loanGoalCode: loanGoal,
		loanGoalName: LOAN_GOALS.find((goal) => goal.value === loanGoal)?.label,
		loanAmount,
		realEstateValue,
		inKHR: undefined,
		khrStatus: undefined,
		minEmploymentRelationship: undefined,
		incomeList,
		maturity,
		productRequisitionTime: undefined,
		age,

		numberOfChildren,
		currentInstallment,
		otherInstallments: undefined,
		currentCreditLine,
		realEstateLocation,
		activeAccountPackageUsage,
		definedAccountPackageUsage,
		activeCreditCardUsage,
		definedInsuranceUsage,

		income,
	};
}

export function getLeadCalculatorResponse(response: MortgageResponseItem): MortgageLeadCalcResponse {
	const {
		subType,
		apr,
		installmentStart,
		allRecovered,
		interestPeriodAided,
		interestPeriodNotAided,
		bank,
		name,
		id,
	} = response;

	return {
		id,
		productName: subType ? `${name} - ${subType}` : name,
		bankCode: bank?.code,
		bankName: bank?.name,
		gift: undefined,
		apr,
		installmentStart,
		allRecovered,
		interestPeriod: interestPeriodAided?.month || interestPeriodNotAided?.month,
		refinanceCalculationId: undefined,
	};
}

export function isCSOKVariant(variant: MortgageListingCalculatorVariant): boolean {
	return variant === "CSOK";
}

export function isHouseLoanVariant(variant: MortgageListingCalculatorVariant): boolean {
	return variant === undefined;
}

export function getDefaultParams(variant: MortgageListingCalculatorVariant): MortgageSearchParams {
	switch (variant) {
		case "EPITES":
			return DEFAULT_PARAMS_FOR_BUILD;

		case "FELUJITAS":
			return DEFAULT_PARAMS_FOR_RENOVATION;

		case "TAMOGATOTT_FELUJITAS":
			return DEFAULT_PARAMS_FOR_SUPPORTED_RENOVATION;

		case "SZAB_FEL":
			return DEFAULT_PARAMS_FOR_FREE_USE;

		case "GREEN_LOAN":
			return DEFAULT_PARAMS_FOR_GREEN_LOAN;

		case "HITELKIVALTAS":
			return DEFAULT_PARAMS_FOR_LOAN_REDEMPTION;
		case "CSOK_PLUSZ":
			return DEFAULT_PARAMS_FOR_CSOK_PLUSZ;
		case "CSOK":
			return DEFAULT_PARAMS_FOR_CSOK;

		case undefined:
			return DEFAULT_PARAMS_FOR_HOUSE_LOAN;

		default:
			return DEFAULT_PARAMS;
	}
}

export async function getRegularBanks(
	calculationDate: string = dayjs().format("YYYY-MM-DD"),
): Promise<FormCheckOptions<string>> {
	const request = {};

	return http
		.get(
			Config.getApiUrl("/api/public/mortgages/core/banks", {
				"calculation-date": calculationDate,
			}),
			request,
		)
		.then((response: Array<{ code: string; name: string }>) =>
			Array.isArray(response)
				? response
						.filter((item) => item.code !== "pannon")
						.map((item) => ({
							label: item.name,
							value: item.code,
						}))
				: undefined,
		);
}

export function getInterestText(result: MortgageResponseItem): string {
	// InterestText adattagba tegyünk bele hogy hány hónpig fix a kamat, ha végig akkor csak egy "-" karaktert
	const { fixInterestRate, interestPeriodNotAided } = result;
	let interestText = "-";

	if (!fixInterestRate) {
		interestText = `${interestPeriodNotAided.month + 1}. hónaptól`;
	}

	return interestText;
}

export function getPeriodNotAidedText(result: MortgageResponseItem): string {
	const { fixInterestRate, interestPeriodNotAided } = result;

	return fixInterestRate ? __("Végig fix") : interestPeriodNotAided.name;
}

export function getLoanGoalName(loanGoal: LoanGoal): string {
	return LOAN_GOALS.find((option) => option.value === loanGoal)?.label;
}

export const MortgagesInfo = {
	getSearchParametersText: (parameters: MortgageRequest): StringPair[] => {
		const loanGoal =
			typeof parameters.loanGoal === "string" ? formatText(parameters.loanGoal, "loangoal") : "Mindegy";

		const data = new JsonDataBuilder();
		data.addTextItem("Hitelösszeg", parameters.loanAmount, "ft");
		data.addTextItem("Futamidő", parameters.maturity, "év");
		data.addTextItem("Ingatlan érték", parameters.realEstateValue, "ft");
		data.addTextItem("Hitelcél", loanGoal);
		data.addTextItem("Jövedelem", parameters.income, "ft");

		return data.getDataArray();
	},

	getProductParametersText: (parameters: MortgageRequest, result: MortgageResponseItem): StringPair[] => {
		const maturityMonths = parameters.maturity * 12;
		let installment = "Egyéb";
		let interestPeriodObject = result.interestPeriodNotAided;

		// Ha nincs kitöltve kitöltjük
		if (result.interestPeriodAided?.month) {
			interestPeriodObject = result.interestPeriodAided;
		}

		if (maturityMonths <= Number(interestPeriodObject.month)) {
			installment = "Végig";
		} else {
			installment = interestPeriodObject.name;
		}

		// Azért van mert a CSOK 25 az Ofk pedig 10
		if (result.loanType === "stateSponsoredLoanHome" && (result.grantYears === 10 || result.grantYears === 25)) {
			installment = "Végig";
		}

		const data = new JsonDataBuilder();
		data.addTextItem("Induló törlesztés", result.installmentStart, "ft");
		data.addTextItem("THM", result.apr, "%");
		data.addTextItem("Teljes visszafizetés", result.fullPayableAmount, "ft");
		data.addTextItem("Fix törlesztő", installment);

		return data.getDataArray();
	},
};
